import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Wand2, FileText, Mail, Loader2 } from "lucide-react";

function renderMarkdown(text) {
  if (!text) return "";
  return text
    .split("\n")
    .map((line) => {
      if (line.startsWith("### ")) return `<h3>${line.slice(4)}</h3>`;
      if (line.startsWith("## ")) return `<h2>${line.slice(3)}</h2>`;
      if (line.startsWith("# ")) return `<h1>${line.slice(2)}</h1>`;
      if (line.match(/^[\*-] /)) return `<li>${line.slice(2)}</li>`;
      if (line.match(/^\d+\. /)) return `<li>${line.replace(/^\d+\. /, "")}</li>`;
      if (line.startsWith("---")) return `<hr/>`;
      let l = line
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/`(.+?)`/g, "<code>$1</code>");
      return l ? `<p>${l}</p>` : "<br/>";
    })
    .join("");
}

export default function AIAssistant() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("cv");
  const [content, setContent] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (content.trim().length < 30) { toast.error(t("ai.min_chars")); return; }
    setLoading(true); setResult("");
    try {
      const r = await api.post("/ai/improve", { type: tab, content, job_title: jobTitle, job_description: jobDescription, language: i18n.language });
      setResult(r.data.result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="ai-page">
      <div className="mec-overline mb-2 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {t("ai.overline")}</div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">{t("ai.title")}</h1>
      <p className="text-slate-600 mb-8">{t("ai.subtitle")}</p>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setResult(""); }}>
        <TabsList className="mb-4">
          <TabsTrigger value="cv" data-testid="tab-cv"><FileText className="w-4 h-4 mr-1.5" /> {t("ai.tab_cv")}</TabsTrigger>
          <TabsTrigger value="cover_letter" data-testid="tab-cover"><Mail className="w-4 h-4 mr-1.5" /> {t("ai.tab_cover")}</TabsTrigger>
        </TabsList>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="mec-card p-6">
            <Label className="text-sm font-semibold">{tab === "cv" ? t("ai.content_label_cv") : t("ai.content_label_cover")}</Label>
            <Textarea rows={14} value={content} onChange={(e) => setContent(e.target.value)} className="mt-1.5 font-mono text-sm" placeholder={tab === "cv" ? t("ai.placeholder_cv") : t("ai.placeholder_cover")} data-testid="ai-content" />

            <div className="grid gap-3 mt-4">
              <div>
                <Label className="text-sm">{t("ai.job_title_label")}</Label>
                <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder={t("ai.job_title_placeholder")} data-testid="ai-job-title" />
              </div>
              {tab === "cover_letter" && (
                <div>
                  <Label className="text-sm">{t("ai.job_desc_label")}</Label>
                  <Textarea rows={3} value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} placeholder={t("ai.job_desc_placeholder")} data-testid="ai-job-desc" />
                </div>
              )}
            </div>

            <Button onClick={submit} disabled={loading} className="mt-4 w-full bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="ai-submit">
              {loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {t("ai.analyzing")}</> : <><Wand2 className="w-4 h-4 mr-1.5" /> {t("ai.submit_btn")}</>}
            </Button>
          </div>

          <div className="mec-card p-6 bg-slate-50/50 min-h-[400px]">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#004EEB]" />
              <span className="font-semibold text-slate-900">{t("ai.result_title")}</span>
            </div>
            {!result && !loading && (
              <div className="text-sm text-slate-500 mt-8 text-center">{t("ai.empty_state")}</div>
            )}
            {loading && (
              <div className="flex flex-col items-center justify-center mt-12 text-slate-500">
                <Loader2 className="w-8 h-8 animate-spin mb-3 text-[#004EEB]" />
                <p className="text-sm">{t("ai.loading_msg")} {tab === "cv" ? t("ai.tab_cv") : t("ai.tab_cover")}...</p>
              </div>
            )}
            {result && (
              <div className="prose-mec" dangerouslySetInnerHTML={{ __html: renderMarkdown(result) }} data-testid="ai-result" />
            )}
          </div>
        </div>
      </Tabs>
    </div>
  );
}
